import isEmpty from 'lodash/isEmpty';
import uniq from 'lodash/uniq';
import isNumber from 'lodash/isNumber';

import {toSentenceCase} from 'common/util/string';
import {findKeyByValue} from 'common/util/typescript';
import {getAllowedTrendUnits} from 'common/report/funnels/util';
import {ChartVizType, ValueRepresentationType} from 'common/report/constants';
import {
  ChartType,
  ChartTypeDisplayName,
  CommonToFunnelChartTypeMap,
  ConversionWindowUnit,
  FunnelsAggregateType,
  FunnelsAggregateTypeDisplayName,
  FunnelsChartSelections,
  FunnelToCommonChartTypeMap,
  StepSelection,
  TableToggleableMetrics,
  TrendUnit,
} from 'common/report/funnels/models/types';
import {
  FREQUENCY_VIZ_TYPES,
  STEPS_VIZ_TYPES,
  TABLE_COLUMN_SELECTOR_DISPLAY_NAME,
  TABLE_COLUMN_SELECTOR_TOOLTIPS,
  TTC_DISTRIBUTION_VIZ_TYPES,
  TTC_MEDIAN_VIZ_TYPES,
} from 'common/report/funnels/models/constants';
import {LiftType, LiftTypeDescription, SegmentLiftBaselineOption} from 'common/report/util/lift/types';
import {getLiftTypeOverridesForFunnels} from 'common/report/funnels/util/lift';
import {FrequencyViewType, FrequencyViewTypeDisplayName} from 'common/report/funnels/models/frequency-chart/types';
import {generateCohortBaselineOptions, isLiftEnabled} from 'common/report/util/lift';
import {FunnelsQuery} from 'common/report/funnels/models/funnels-query';
import {groupByToBookmarkGroupBy} from 'common/query-builder/query-block/segment-menu/util';
import {isCohortSegment} from 'common/query-builder/query-block/segment-menu/types';
import {formatTime, getTimeUnit} from 'common/util/date';
import {SelectTheme} from 'common/components/select/constants';
import {DisplaySize, Placement} from 'common/components/tooltip/types';
import {TrendChartTypeSelection, TrendCountTypeSelection} from 'common/report/funnels/models/trends';
import {ChartHeaderUpdateDetail} from 'common/widgets/chart-header';
import {BucketSelectorAttrs} from 'common/widgets/bucket-selector';
import {SelectAttrs} from 'common/components/select/types';
import {StepRangeSelectorAttrs} from 'common/widgets/step-range-selector';
import {useFunnelsConsistency} from 'common/report/util/feature-sampling';

import {TrackedEvents} from '../util/tracking';

export function chartHeaderAttrs({funnelQuery, rawQueryResult}: {funnelQuery: FunnelsQuery; rawQueryResult}) {
  const chartType = funnelQuery.chartType;

  let extraChartAttrs = {};
  const disabledCharts = {};
  function disableChartTypes(chartTypes: ChartType[], explanation: string) {
    chartTypes.forEach((chartType) => {
      const commonChartType = FunnelToCommonChartTypeMap[chartType];
      disabledCharts[commonChartType] = {
        isDisabled: true,
        tooltip: {
          placement: `left`,
          text: `${ChartTypeDisplayName[chartType]} ${explanation}`,
        },
      };
    });
  }

  if (!funnelQuery.isAnyOrderFunnel()) {
    disableChartTypes([ChartType.Sankey], ` is only available with any order funnels`);
  }

  if (funnelQuery.liftComparison) {
    const explanation = ` is not available with ${
      LiftTypeDescription[funnelQuery.liftComparison?.type || LiftType.Time]
    }`;
    disableChartTypes([ChartType.Sankey, ChartType.TimeToConvert, ChartType.Frequency], explanation);
  }

  switch (chartType) {
    case ChartType.TimeToConvert: {
      if (funnelQuery.ttcAggregateType !== FunnelsAggregateType.Median) {
        extraChartAttrs = {
          'value-representations': Object.values(ValueRepresentationType),
          'selected-value-representation': funnelQuery.ttcValueRepresentation,
        };
      }
      break;
    }
    case ChartType.Frequency: {
      if (
        funnelQuery.frequencyType !== FrequencyViewType.ConversionRate &&
        funnelQuery.frequencyAggregateType !== FunnelsAggregateType.Median
      ) {
        extraChartAttrs = {
          'value-representations': Object.values(ValueRepresentationType),
          'selected-value-representation': funnelQuery.frequencyValueRepresentation,
        };
      }
      break;
    }
    case ChartType.Trends: {
      const enabledUnits = getAllowedTrendUnits(funnelQuery.dateRange.getIntervalDays());
      const disabledMessages = {
        minute: `Minute queries can be performed on strict 24 hour periods`,
        hour: `Date range must be <= 1 week`,
        day: `Date range must be <= 90 days`,
        week: `Date range must be <= 90 weeks`,
      };
      extraChartAttrs = {
        'date-units': Object.values(TrendUnit),
        'date-unit-overrides': Object.values(TrendUnit).reduce((overrides, unit) => {
          if (!enabledUnits.includes(unit)) {
            overrides[unit] = {
              isDisabled: true,
              value: unit,
              label: toSentenceCase(unit),
              tooltip: {text: disabledMessages[unit], placement: `left`},
            };
          }

          return overrides;
        }, {}),
        'selected-date-unit': funnelQuery.trendUnit,
      };
      break;
    }
    case ChartType.Sankey: {
      extraChartAttrs = {'time-comparison-disabled-reason': `Compare to Past is not supported for Top Paths`};
      break;
    }
  }

  const {supportedVizTypes, selectedChartVizType} = getChartVizTypeConfig(chartType, funnelQuery);
  return {
    'chart-types': FunnelsChartSelections,
    'chart-type-overrides': disabledCharts,
    'chart-viz-types': supportedVizTypes,
    ...(selectedChartVizType ? {'selected-chart-viz-type': selectedChartVizType} : {}),
    'selected-chart-type': findKeyByValue(CommonToFunnelChartTypeMap, chartType),
    'time-comparison-tooltip-text': `Compare this funnel conversion rate to a past time period`,
    'project-id': funnelQuery.projectId,
    'show-date-range-selector': true,
    'enable-lift-comparison': isLiftEnabled(),
    'selected-lift-comparison': funnelQuery.liftComparison,
    'lift-type-overrides': getLiftTypeOverridesForFunnels(funnelQuery),
    breakdowns: funnelQuery.groupBy || [],
    'selected-breakdown-segments': getLeafLevelSegments(funnelQuery, rawQueryResult),
    'date-range': funnelQuery.dateRange.getAttributes(),
    'comparison-date-range': funnelQuery.comparedDateRange,
    ...extraChartAttrs,
  };
}

export function bookmarkUpdateFromHeaderUpdate(
  update: ChartHeaderUpdateDetail,
  funnelQuery: FunnelsQuery,
): Partial<FunnelsQuery> {
  const selectedChartType = CommonToFunnelChartTypeMap[update.chartType];
  const queryUpdates: Partial<FunnelsQuery> = {
    dateRange: update.dateRange,
    chartType: selectedChartType,
    ...(update.dateUnit ? {trendUnit: update.dateUnit as TODO} : {}),
    comparedDateRange: update.comparisonDateRange,
  };
  const valueRepresentation = update.valueRepresentation;

  if (
    funnelQuery.chartType !== selectedChartType &&
    (selectedChartType === ChartType.TimeToConvert || selectedChartType === ChartType.Frequency)
  ) {
    queryUpdates.liftComparison = null;
  }

  if (funnelQuery.chartType === ChartType.TimeToConvert) {
    queryUpdates.ttcValueRepresentation = valueRepresentation;
  } else if (funnelQuery.chartType === ChartType.Frequency) {
    queryUpdates.frequencyValueRepresentation = valueRepresentation;
  }

  if (update.chartVizType) {
    // validate that this is a supported viz type since mp-chart-header can emit an update
    // with an old unsupported vizType when switching charts. TODO: granular update events for mp-chart-header
    // per the comment here https://github.com/mixpanel/analytics/blob/fd9a29b700c0cc08183f298dbf54b1c1080016de/iron/common/widgets/chart-header/index.ts#L328-L331
    const {supportedVizTypes} = getChartVizTypeConfig(selectedChartType, funnelQuery);
    if (supportedVizTypes.includes(update.chartVizType)) {
      switch (selectedChartType) {
        case ChartType.TimeToConvert:
          queryUpdates.ttcVizType = update.chartVizType;
          break;
        case ChartType.Frequency:
          queryUpdates.frequencyVizType = update.chartVizType;
          break;
        case ChartType.Chart:
          queryUpdates.stepsVizType = update.chartVizType;
          break;
        default:
      }
    }
  }

  if (Object.prototype.hasOwnProperty.call(update.delta, `liftComparison`)) {
    queryUpdates.liftComparison = update.delta.liftComparison;
  }

  return queryUpdates;
}

function getLeafLevelSegments(funnelQuery: FunnelsQuery, rawQueryResult): SegmentLiftBaselineOption[] {
  const breakdowns = funnelQuery.groupBy || [];
  if (!breakdowns.length) {
    return [];
  }

  const bookmarkGroupBy = groupByToBookmarkGroupBy(breakdowns, true);

  const lastBreakdown = bookmarkGroupBy[breakdowns.length - 1];
  if (isCohortSegment(lastBreakdown)) {
    return generateCohortBaselineOptions(lastBreakdown);
  } else {
    const propertyValues = rawQueryResult?.meta?.property_values;
    if (propertyValues) {
      const leafProperties = propertyValues.map((property) => {
        const properties = property.split(`,`);
        return properties[properties.length - 1];
      });

      if (leafProperties.length) {
        return uniq(leafProperties).map((propertyValue) => {
          return {propertyValue: propertyValue as string, displayName: propertyValue as string};
        });
      }
    }
  }
  return [];
}

function canSelectVizType(chartType: ChartType, funnelQuery: FunnelsQuery) {
  if (chartType === ChartType.Frequency) {
    return funnelQuery.frequencyAggregateType === FunnelsAggregateType.Distribution;
  }

  return [ChartType.Chart, ChartType.TimeToConvert].includes(chartType);
}

function getChartVizTypeConfig(chartType: ChartType, funnelQuery: FunnelsQuery) {
  let selectedChartVizType;
  let supportedVizTypes = [];

  if (canSelectVizType(chartType, funnelQuery)) {
    switch (chartType) {
      case ChartType.Chart:
        selectedChartVizType = funnelQuery.stepsVizType ?? ChartVizType.Steps;
        supportedVizTypes = STEPS_VIZ_TYPES;
        break;
      case ChartType.Frequency:
        selectedChartVizType = funnelQuery.frequencyVizType;
        supportedVizTypes = FREQUENCY_VIZ_TYPES;
        break;
      case ChartType.TimeToConvert:
        selectedChartVizType = funnelQuery.ttcVizType;
        if (funnelQuery.ttcAggregateType === FunnelsAggregateType.Median) {
          supportedVizTypes = TTC_MEDIAN_VIZ_TYPES;
        } else {
          supportedVizTypes = TTC_DISTRIBUTION_VIZ_TYPES;
        }
        break;
    }
  }

  return {selectedChartVizType, supportedVizTypes};
}

export function showStepSelector(funnelQuery: FunnelsQuery): boolean {
  if (useFunnelsConsistency()) {
    return false;
  }
  // Basically only don't show for steps viz
  return funnelQuery.chartType !== ChartType.Chart || funnelQuery.stepsVizType === ChartVizType.Metric;
}

export function showBucketSelector(funnelQuery: FunnelsQuery, hasChartData: boolean): boolean {
  const {chartType, ttcAggregateType, frequencyAggregateType} = funnelQuery;
  return (
    hasChartData &&
    ((chartType === ChartType.TimeToConvert && ttcAggregateType === FunnelsAggregateType.Distribution) ||
      // Don't show buckets for median, etc aggregation
      (chartType === ChartType.Frequency && frequencyAggregateType === FunnelsAggregateType.Distribution))
  );
}
export function showAggregateSelector(funnelQuery: FunnelsQuery): boolean {
  const {chartType, frequencyVizType, ttcVizType} = funnelQuery;
  return (
    (chartType === ChartType.Frequency && frequencyVizType !== ChartVizType.Line) ||
    (chartType === ChartType.TimeToConvert && ttcVizType === ChartVizType.Bar)
  );
}

export function getBucketSelectorAttrs(funnelQuery: FunnelsQuery): BucketSelectorAttrs {
  const commonAttrs = {
    'range-min': 0,
    header: `Define Interval`,
    'tooltip-text': `Define a custom bucket size`,
  };

  switch (funnelQuery.chartType) {
    case ChartType.Frequency:
      return {
        ...commonAttrs,
        'selected-bucket': funnelQuery.frequencyBuckets,
        'selected-unit': `frequency`,
      };

    case ChartType.TimeToConvert:
      return {
        ...commonAttrs,
        units: Object.values(ConversionWindowUnit).filter((key) => key !== `session`),
        'selected-unit': getTimeUnit(funnelQuery.timeBuckets.bucketSize),
        'selected-bucket': funnelQuery.timeBuckets,
        'reset-bucket': funnelQuery.timeBuckets?.isDrilledDown,
      };
    default:
      throw new Error(`Invalid ChartType for Bucket Selection`);
  }
}

export function getSubChartSelectorAttrs(funnelQuery: FunnelsQuery): SelectAttrs {
  const sharedAttrs = {
    elref: `subChartSelector`,
    theme: SelectTheme.Secondary,
  };

  switch (funnelQuery.chartType) {
    case ChartType.Frequency: {
      const selected = FrequencyViewTypeDisplayName[FrequencyViewType[funnelQuery.frequencyType]].label;

      // Conversion rates don't support median, or any Lift mode
      const shouldConversionRateBeDisabled =
        funnelQuery.frequencyAggregateType === FunnelsAggregateType.Median || !isEmpty(funnelQuery.liftComparison);

      const items = Object.entries(FrequencyViewTypeDisplayName).map(([value, {label}]) => ({
        value,
        label,
        isSelected: label === selected,
        isDisabled: shouldConversionRateBeDisabled && value === FrequencyViewType.ConversionRate,
      }));
      return {
        ...sharedAttrs,
        sections: [{items}],
        'menu-width': 160,
      };
    }
    case ChartType.Trends: {
      const selected = TrendChartTypeSelection[funnelQuery.trendType];
      const items = Object.entries(TrendChartTypeSelection).map(([value, item]) => ({
        ...item,
        value,
        isSelected: item.label === selected.label,
      }));
      return {
        ...sharedAttrs,
        sections: [{items}],
        'menu-width': 200,
        tooltip: {...selected.tooltip, placement: Placement.Top},
      };
    }
    default:
      throw new Error(`Invalid ChartType for subchart selector mapping`);
  }
}

export function getAggregateTypeSelectorAttrs(funnelQuery: FunnelsQuery): SelectAttrs {
  const chartType = funnelQuery.chartType;
  if (chartType === ChartType.Frequency || chartType === ChartType.TimeToConvert) {
    const aggregateType =
      chartType === ChartType.Frequency
        ? funnelQuery.frequencyAggregateType ?? FunnelsAggregateType.Distribution
        : funnelQuery.ttcAggregateType ?? FunnelsAggregateType.Distribution;

    const selected = FunnelsAggregateTypeDisplayName[FunnelsAggregateType[aggregateType]].label;

    // Medians don't support conversion rate
    const shouldMedianBeDisabled =
      chartType === ChartType.Frequency && funnelQuery.frequencyType === FrequencyViewType.ConversionRate;

    const items = Object.entries(FunnelsAggregateTypeDisplayName).map(([value, item]) => ({
      value,
      label: item.label,
      isSelected: item.label === selected,
      isDisabled: shouldMedianBeDisabled && value === FunnelsAggregateType.Median,
      ...(shouldMedianBeDisabled && value === FunnelsAggregateType.Median
        ? {tooltip: {text: `Cannot select medians for conversion rate.`}}
        : {}),
    }));
    return {
      theme: SelectTheme.Secondary,
      sections: [{items}],
      'menu-width': 160,
      elref: `aggregateSelector`,
    } as SelectAttrs;
  } else {
    throw new Error(`Invalid chart type for aggregator selector`);
  }
}

export function stepSelectorAttributes(funnelQuery: FunnelsQuery): StepRangeSelectorAttrs {
  const totalSteps = funnelQuery.events.length;
  const {eventIndex} = funnelQuery;

  let selected: StepSelection;
  const disableAllSteps = funnelQuery.chartType === ChartType.Frequency;
  if (isNumber(eventIndex)) {
    selected = {
      isEntireRange: false,
      range: {
        from: eventIndex + 1,
        to: eventIndex + 2, // step-selector is 1-indexed
      },
    };
  } else if (disableAllSteps) {
    // Some charts show nothing for All steps.
    // This is a hacky way of showing them they are looking at step 1-2 when they are coming from all steps
    // without actually changing their chosen step index
    selected = {
      isEntireRange: false,
      range: {
        from: 1,
        to: 2,
      },
    };
  } else {
    selected = {isEntireRange: true, range: {}};
  }

  return {
    'disable-all-steps': disableAllSteps,
    'num-steps': totalSteps,
    selected,
    tooltip: `Limit your query to between particular steps`,
  };
}

export function getTableColumnSelectorAttrs(funnelQuery: FunnelsQuery): SelectAttrs {
  const items = Object.values(TableToggleableMetrics).map((column) => {
    return {
      isSelected: funnelQuery.selectedTableColumns[column],
      label: TABLE_COLUMN_SELECTOR_DISPLAY_NAME[column],
      tooltip: {
        text: TABLE_COLUMN_SELECTOR_TOOLTIPS[column],
        placement: Placement.Left,
        size: DisplaySize.Small,
      },
      value: column,
      ...(column === TableToggleableMetrics.StatSig && (!funnelQuery.isSegmented() || !funnelQuery.shouldShowStatSig())
        ? {isDisabled: true}
        : {}),
    };
  });
  return {
    elref: `tableColumnSelector`,
    icon: {leftIcon: `column-select`},
    'icon-only': true,
    theme: SelectTheme.Secondary,
    sections: [{items}],
    multiple: true,
    'menu-width': 270,
    tooltip: {text: `Pick which metrics are shown in the table below.`},
  } as SelectAttrs;
}

export function getTrendCountTypeAttrs(funnelQuery: FunnelsQuery): SelectAttrs {
  const selected = TrendCountTypeSelection[funnelQuery.trendCountType];
  const items = Object.entries(TrendCountTypeSelection).map(([value, {label, icon, tooltip}]) => ({
    label,
    value,
    icon,
    tooltip,
    isSelected: label === selected.label,
  }));
  return {
    elref: `trendCountType`,
    theme: SelectTheme.Secondary,
    sections: [{items}],
    icon: {leftIcon: selected.icon},
    'menu-width': 160,
  } as SelectAttrs;
}

export type ChildHeaderUpdate = {update: Partial<FunnelsQuery>; trackEvent?: {eventName; props}};

export function updateStepSelection(ev, funnelQuery: FunnelsQuery): ChildHeaderUpdate {
  const {detail: range} = ev;
  const previousIndex = funnelQuery.eventIndex;
  const previousStep = previousIndex ? `Step ${previousIndex + 1} to Step ${previousIndex + 2}` : `All steps`;

  if (range.isEntireRange) {
    return {
      update: {eventIndex: null},
      trackEvent: {
        eventName: TrackedEvents.StepSelected,
        props: {'Step selection': `All steps`, 'Previous Step selection': previousStep},
      },
    };
  }
  const eventIndex = range.range.from - 1;

  return {
    update: {eventIndex},
    trackEvent: {
      eventName: TrackedEvents.StepSelected,
      props: {
        'Step selection': `Step ${eventIndex + 1} to Step ${eventIndex + 2}`,
        'Previous Step selection': previousStep,
      },
    },
  };
}

export function selectChartView(ev, funnelQuery: FunnelsQuery): ChildHeaderUpdate {
  let previousChartView = ``;
  const newChartView = ev.detail.item.value || ev.detail.value;
  let update;
  switch (funnelQuery.chartType) {
    case ChartType.Trends:
      previousChartView = funnelQuery.trendType;
      update = {
        trendType: newChartView,
      };
      break;
    case ChartType.Frequency:
      previousChartView = funnelQuery.frequencyType;
      update = {
        frequencyType: newChartView,
      };
      break;
    default:
      throw new Error(`Invalid ChartType for Chart View`);
  }

  return {
    update,
    trackEvent: {
      eventName: TrackedEvents.ChartViewSelected,
      props: {
        'Previous chart view': previousChartView,
        'New chart view': newChartView,
      },
    },
  };
}

export function updateTimeBuckets(ev, funnelQuery: FunnelsQuery): ChildHeaderUpdate {
  const updates = ev.detail;
  let oldBucket = null;
  const {chartType, frequencyBuckets, timeBuckets} = funnelQuery;
  let paramUpdates = {};
  switch (chartType) {
    case ChartType.Frequency: {
      oldBucket = frequencyBuckets;
      paramUpdates = {
        frequencyBuckets: updates,
      };
      break;
    }
    case ChartType.TimeToConvert: {
      oldBucket = timeBuckets;
      paramUpdates = {
        timeBuckets: {...updates, isDrilledDown: true},
      };
      break;
    }
    default:
      throw new Error(`Invalid ChartType for bucket mapping`);
  }

  return {
    update: paramUpdates,
    trackEvent: {
      eventName: TrackedEvents.EditBucketSelector,
      props: {
        'Previous chart interval size': oldBucket.bucketSize,
        'Previous chart interval maximum': oldBucket.max,
        'Previous chart interval minimum': oldBucket.min,
      },
    },
  };
}

export function resetTimeBuckets(_ev, funnelQuery: FunnelsQuery): ChildHeaderUpdate {
  const {chartType, timeBuckets} = funnelQuery;
  if (chartType !== ChartType.TimeToConvert) {
    return null;
  }

  const previousSelection = timeBuckets.max ? formatTime(timeBuckets.min, timeBuckets.max) : null;

  return {
    update: {
      timeBuckets: null,
    },
    trackEvent: {
      eventName: TrackedEvents.TTCReset,
      props: {
        previous_selection: previousSelection,
        event_index: funnelQuery.eventIndex !== null ? funnelQuery.eventIndex + 1 : `overall`,
      },
    },
  };
}

export function selectTrendCountType(ev, funnelQuery: FunnelsQuery): ChildHeaderUpdate {
  const previousTrendCountType = funnelQuery.trendCountType;
  const newTrendCountType = ev.detail.item.value;
  const update = {
    trendCountType: newTrendCountType,
  };

  const trackEvent = {
    eventName: TrackedEvents.TrendCountTypeToggled,
    props: {
      'Previous trend count type': previousTrendCountType,
      'New trend count type': newTrendCountType,
    },
  };

  return {update, trackEvent};
}

export function selectAggregateType(ev, funnelQuery: FunnelsQuery): ChildHeaderUpdate {
  switch (funnelQuery.chartType) {
    case ChartType.Frequency:
      return {
        update: {
          frequencyAggregateType: ev.detail.item.value,
        },
      };
    case ChartType.TimeToConvert:
      return {
        update: {
          ttcAggregateType: ev.detail.item.value,
        },
      };
  }
  return null;
}

export function updateSelectedColumn(ev, funnelQuery: FunnelsQuery): ChildHeaderUpdate {
  const column = ev.detail.item.value;
  const selectedTableColumns = funnelQuery.selectedTableColumns;
  const toggleTo = !selectedTableColumns[column];
  return {
    update: {
      selectedTableColumns: {...selectedTableColumns, [column]: toggleTo},
    },
    trackEvent: {
      eventName: toggleTo ? TrackedEvents.ShowTableMetric : TrackedEvents.HideTableMetric,
      props: {
        metric: column,
      },
    },
  };
}
